<script setup>
  import ComponentBreadcrumb from "@components/Breadcrumb/Breadcrumb.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import DocumentComponentUrlRedirect from "@pages/Document/Components/UrlRedirect.vue";

  defineOptions({
    name: "PagesDocumentIndex",
  });

  const props = defineProps({
    urlRedirects: {
      type: Array,
      default: () => {
        return [];
      },
    },
  });
  function hasUrlRedirect(identifier) {
    let found = props.urlRedirects.some((object) => {
      return object.identifier === identifier;
    });

    if (!found) {
      console.error('Missing identifier in url redirects: "' + identifier + '".');
    }

    return found;
  }

  function getUrlRedirect(identifier) {
    return props.urlRedirects.find((object) => {
      return object.identifier === identifier;
    });
  }
</script>

<template>
  <ComponentBreadcrumb
    class="mt-2"
    :links="[
      {
        label: 'Dokumente / Arbeitshilfen',
      },
    ]"
  />

  <div id="p4umc-documents" class="my-4">
    <component-card title="Dokumente / Arbeitshilfen" :test-id="getTestId()">
      <ol class="space-y-8 pb-8">
        <li>
          <h2 class="text-lg">I. MediCheck-Dokumente</h2>
          <ul class="mt-2 ml-6 list-disc space-y-4">
            <li v-if="$page.props.client.license.is_pro && hasUrlRedirect('medicheck-overview')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('medicheck-overview')"
                :test-id="getTestId('medicheck-overview')"
              />
            </li>

            <!-- Gebrauchsanweisung (2 Versionen) -->
            <li v-if="$page.props.client.license.is_connect && hasUrlRedirect('manual-medicheck-wawi')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('manual-medicheck-wawi')"
                :test-id="getTestId('medicheck-manual-wawi')"
              />
            </li>

            <li v-else-if="$page.props.client.license.is_pro && hasUrlRedirect('manual-medicheck')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('manual-medicheck')"
                :test-id="getTestId('medicheck-manual')"
              />
            </li>

            <li v-if="$page.props.client.license.is_pro && hasUrlRedirect('short-manual-medicheck')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('short-manual-medicheck')"
                :test-id="getTestId('medicheck-manual-short')"
              />
            </li>

            <li v-if="hasUrlRedirect('declaration-of-conformity')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('declaration-of-conformity')"
                :test-id="getTestId('declaration-of-conformity')"
              />
            </li>

            <li>
              <!-- override with customized (!) privacy-consent url via DocumentController -->
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('privacy-consent')"
                :override-target-url="$route('documents.show', {document: 'customized-privacy-consent'})"
                :test-id="getTestId('privacy-consent')"
              />
            </li>

            <li v-if="hasUrlRedirect('pdl-agreement-short')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('pdl-agreement-short')"
                :test-id="getTestId('pdl-agreement-short')"
              />
            </li>

            <li v-if="hasUrlRedirect('pdl-agreement-nursing-home-short')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('pdl-agreement-nursing-home-short')"
                :test-id="getTestId('pdl-agreement-nursing-home-short')"
              />
            </li>

            <li v-if="$page.props.client.license.is_connect && hasUrlRedirect('training-video-medicheck-wawi')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('training-video-medicheck-wawi')"
                :test-id="getTestId('training-video-medicheck-wawi')"
              />
            </li>

            <li v-if="$page.props.client.license.is_connect && hasUrlRedirect('updates-medicheck-nident')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('updates-medicheck-nident')"
                :test-id="getTestId('updates-medicheck-nident')"
              />
            </li>

            <li v-else-if="$page.props.client.license.is_pro && hasUrlRedirect('updates-medicheck-p4u')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('updates-medicheck-p4u')"
                :test-id="getTestId('updates-medicheck-p4u')"
              />
            </li>
          </ul>
        </li>

        <li>
          <h2 class="text-lg">II. Honorierte pharmazeutische Dienstleistungen gemäß ABDA</h2>

          <ul class="mt-2 ml-6 list-disc space-y-4">
            <li v-if="hasUrlRedirect('abda-pdl-overview')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-pdl-overview')"
                :test-id="getTestId('abda-pdl-overview')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-pdl-polymedication')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-pdl-polymedication')"
                :test-id="getTestId('abda-pdl-polymedication')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-care-for-organ-transplant-patients')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-care-for-organ-transplant-patients')"
                :test-id="getTestId('abda-care-for-organ-transplant-patients')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-care-for-oral-antitumour-therapy')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-care-for-oral-antitumour-therapy')"
                :test-id="getTestId('abda-care-for-oral-antitumour-therapy')"
              />
            </li>
          </ul>
        </li>

        <li>
          <h2 class="text-lg">III. Weitere honorierte pharmazeutische Dienstleistungen gemäß ABDA</h2>

          <ul class="mt-2 ml-6 list-disc space-y-4">
            <li v-if="hasUrlRedirect('abda-pdl-inhalants')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-pdl-inhalants')"
                :test-id="getTestId('abda-pdl-inhalants')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-pdl-blood-pressure')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-pdl-blood-pressure')"
                :test-id="getTestId('abda-pdl-blood-pressure')"
              />
            </li>
          </ul>
        </li>

        <li>
          <h2 class="text-lg">IV. Leitlinien und Arbeitshilfen zur Medikationsanalyse (allgemein)</h2>
          (ACHTUNG: für pharmazeutische Dienstleistung siehe oben unter I. und/oder II.)
          <ul class="mt-2 ml-6 list-disc space-y-4">
            <li v-if="hasUrlRedirect('abda-guideline-amts')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-guideline-amts')"
                :test-id="getTestId('abda-guideline-amts')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-guideline-amts-comment')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-guideline-amts-comment')"
                :test-id="getTestId('abda-guideline-amts-comment')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-amts-flow-chart')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-amts-flow-chart')"
                :test-id="getTestId('abda-amts-flow-chart')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-process-description-amts-in-nursing-homes')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-process-description-amts-in-nursing-homes')"
                :test-id="getTestId('abda-process-description-amts-in-nursing-homes')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-sop-amts-in-nursing-homes')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-sop-amts-in-nursing-homes')"
                :test-id="getTestId('abda-sop-amts-in-nursing-homes')"
              />
            </li>

            <li v-if="hasUrlRedirect('abda-checklist-amts-in-nursing-homes')">
              <document-component-url-redirect
                :url-redirect="getUrlRedirect('abda-checklist-amts-in-nursing-homes')"
                :test-id="getTestId('abda-checklist-amts-in-nursing-homes')"
              />
            </li>
          </ul>
        </li>
      </ol>
    </component-card>
  </div>
</template>
